/**
 * Advisor: a compact, dismissible onboarding card that guides a new player
 * through their first solo game by READING GameState once per sim-second —
 * it never hooks commands, so it works identically for mouse and touch and
 * can't drift from what the sim actually did.
 *
 * Solo games show it by default; multiplayer games do not (still reachable
 * via the Help button, which also opens the static quick-reference overlay).
 * Progress (dismissed / current step) is persisted per-browser in
 * localStorage so a player who skips it, or finishes it once, is never
 * nagged again — but it's untouched by starting a *new* game, so a fresh
 * solo game re-evaluates each step against that game's own state.
 */

import { TICKS_PER_SECOND, activePhaseWork, dist, entityCenter, playerBuildings, playerUnits } from '../sim';
import type { GameState, PlayerIdx, Vec2 } from '../sim';
import { effectiveTileSize, worldToScreen } from './camera';
import type { GameSession } from './session';

const DISMISS_KEY = 'tokenomics.advisor.dismissed';
// Bumped for Economy 2.0's new "Feed the model" step: existing players with a
// persisted step index from the old (shorter) STEPS array would otherwise
// land on the wrong card after the insert -- a fresh key means everyone just
// re-evaluates from the top against their current game state (see the
// fast-forward loop in the constructor), which is a one-time no-op for
// anyone who'd already finished the tutorial.
const STEP_KEY = 'tokenomics.advisor.step.v2';
const FLASH_MS = 900;

interface AdvisorStep {
  title: string;
  tip: string;
  progress: (s: GameState, me: PlayerIdx) => string;
  done: (s: GameState, me: PlayerIdx) => boolean;
  beacon?: (s: GameState, me: PlayerIdx) => Vec2 | null;
}

/** Nearest point of a given neutral entity kind to the player's HQ (or first unit, if no HQ yet). */
function nearestOfKind(s: GameState, me: PlayerIdx, kind: 'dataNode' | 'market'): Vec2 | null {
  const hq = playerBuildings(s, me).find((b) => b.type === 'hq');
  const from = hq ? entityCenter(hq) : (playerUnits(s, me)[0]?.pos ?? null);
  if (!from) return null;
  let best: Vec2 | null = null;
  let bestD = Infinity;
  for (const e of s.entities) {
    if (e.kind !== kind) continue;
    const c = entityCenter(e);
    const d = dist(from, c);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

const STEPS: AdvisorStep[] = [
  {
    title: 'Harvest data',
    tip: 'Select an engineer, then right-click (or tap) a data crystal to gather. Data funds research and phase advances.',
    progress: (s, me) => `Data: ${Math.round(s.players[me].data)}`,
    done: (s, me) => {
      if (s.players[me].data > 0) return true;
      return playerUnits(s, me).some(
        (u) => u.type === 'engineer' && (u.order.kind === 'gather' || u.order.kind === 'returnData'),
      );
    },
    beacon: (s, me) => nearestOfKind(s, me, 'dataNode'),
  },
  {
    title: 'Add token capacity',
    tip: 'Build a GPU Cluster. Clusters generate tokens but burn $ every second — capacity is never free.',
    progress: (s, me) =>
      playerBuildings(s, me).some((b) => b.type === 'gpuCluster') ? 'Under construction…' : 'Not started',
    done: (s, me) => playerBuildings(s, me).some((b) => b.type === 'gpuCluster'),
  },
  {
    title: 'Stand up serving',
    tip: 'Build an Inference Endpoint and train an Inference Agent there — that agent is how tokens become revenue.',
    progress: (s, me) =>
      playerBuildings(s, me).some((b) => b.type === 'inferenceEndpoint')
        ? 'Endpoint up — train an agent'
        : 'Build an Inference Endpoint',
    done: (s, me) => playerUnits(s, me).some((u) => u.type === 'inferenceAgent'),
  },
  {
    title: 'Earn revenue',
    tip: "Send the agent to a market (gold ring). Watch net $/s in the top bar — if it's red, you're on a countdown.",
    progress: (s, me) => `Revenue: ${s.players[me].revenuePerSec.toFixed(1)}/s`,
    done: (s, me) => s.players[me].revenuePerSec > 0,
    beacon: (s, me) => nearestOfKind(s, me, 'market'),
  },
  {
    title: 'Feed the model',
    tip: 'Serving consumes data; keep gatherers running or revenue goes stale. Build a Data Warehouse to expand storage.',
    progress: (s, me) => `Data: ${Math.round(s.players[me].data)}/${Math.round(s.players[me].dataCap)}`,
    done: (s, me) =>
      playerBuildings(s, me).some((b) => b.type === 'dataWarehouse' && b.buildTicks === 0) ||
      s.players[me].data > 100,
  },
  {
    title: 'Practice FinOps',
    tip: 'Build the FinOps Command Center and research Tagging Policy. Optimization techs are how margin is won.',
    progress: (s, me) =>
      playerBuildings(s, me).some((b) => b.type === 'finopsCenter')
        ? 'Center up — research Tagging Policy'
        : 'Build a FinOps Command Center',
    done: (s, me) => s.players[me].techs.includes('taggingPolicy'),
  },
  {
    title: 'Mature the practice',
    tip: 'Advance to Walk when you can afford it — it unlocks optimization techs, caches, and defenses.',
    progress: (s, me) => {
      const work = activePhaseWork(s, me);
      return work ? 'Advancing…' : `Phase: ${['Crawl', 'Walk', 'Run'][s.players[me].phase]}`;
    },
    done: (s, me) => s.players[me].phase >= 1,
  },
  {
    title: 'Win',
    tip: "Destroy the rival Cloud Console, or outlast them — a rival is eliminated after 45s below $0. Mind anomalies: runaway agents burn tokens until you terminate them.",
    progress: () => 'Good luck, Practitioner.',
    done: () => false, // final card: stays until dismissed
  },
];

const HELP_HTML = `
  <div class="help-box">
    <div class="help-head">
      <h2>Quick Reference</h2>
      <button class="advisor-close" id="help-close" aria-label="Close" title="Close">&#10005;</button>
    </div>

    <h3>Controls — desktop</h3>
    <ul>
      <li>Left-drag: box-select units. Left-click: select one.</li>
      <li>Right-click: contextual command (move / gather / serve / attack) for the current selection.</li>
      <li>WASD / arrow keys, or edge-scroll: pan the camera. Scroll wheel: zoom.</li>
      <li>Tab: scoreboard. H: select your Cloud Console. Esc: clear selection or cancel placement.</li>
    </ul>

    <h3>Controls — touch</h3>
    <ul>
      <li>Tap your unit/building to select it; double-tap a unit selects all visible units of that type.</li>
      <li>With a selection, tap the map/a target to move, gather, serve, or attack — same as right-click.</li>
      <li>One-finger drag pans the camera; two-finger pinch zooms.</li>
      <li>Tap the minimap to jump the camera there.</li>
    </ul>

    <h3>Resources</h3>
    <ul>
      <li><strong>$ (budget)</strong> — your cloud bill. Net $/s is revenue minus burn; stay below $0 too long and you go bankrupt.</li>
      <li><strong>Tokens</strong> — capacity from GPU Clusters, spent by agents to serve markets or just idle.</li>
      <li><strong>Data</strong> — harvested from data crystals by engineers; funds research and phase advances, and is also serving fuel (capped — build Data Warehouses to raise the cap). Run dry and your model goes stale: revenue craters until data flows again.</li>
      <li><strong>Power</strong> — every operational building draws it; your Cloud Console and Substations supply it. Draw past cap causes a brownout that throttles token generation, training, and fine-tuning.</li>
      <li><strong>Doctrine</strong> — once available, a permanent choice between RAG (data-hungry, +revenue) and Fine-Tuning (data-light, unlocks fine-tuning jobs at the Training Cluster).</li>
    </ul>

    <h3>Living map &amp; tactics</h3>
    <ul>
      <li>Markets migrate every couple minutes — don't over-fortify one spot; the demand you're camping can relocate out from under you.</li>
      <li>The Load Tester is a siege unit: it outranges Guardrail Towers and hits buildings only, so it can crack a fortified position that would otherwise grind down attackers — but it's helpless against units, so escort it.</li>
      <li>Stances (Aggressive / Defend / Passive) control how your units react to nearby threats — set Defend to leash an escort near a base, or Passive to stop a unit from picking fights it can't win.</li>
    </ul>

    <h3>Win / loss</h3>
    <ul>
      <li>Destroy the rival Cloud Console, or starve them out — a player still below $0 after 45s is eliminated.</li>
      <li>The same rules apply to you: keep net $/s positive, or keep enough runway to ride out a dip.</li>
      <li>Watch for cost anomalies: a runaway agent burns tokens and earns nothing until it self-resolves or you hit Terminate.</li>
    </ul>

    <div class="help-close-row">
      <button class="btn-hud" id="help-close-2">Close</button>
    </div>
  </div>
`;

export class Advisor {
  private dismissed: boolean;
  private stepIndex: number;
  private flashUntil = 0;
  private flashTitle = '';
  private lastEvalTick = -1;
  private lastRenderTick = -1;

  private cardEl: HTMLElement;
  private beaconEl: HTMLElement;
  private helpEl: HTMLElement;

  constructor(
    private hudRoot: HTMLElement,
    private session: GameSession,
  ) {
    this.dismissed = safeGet(DISMISS_KEY) === '1';
    const storedStep = Number(safeGet(STEP_KEY) ?? '0');
    this.stepIndex = Number.isFinite(storedStep) ? Math.min(Math.max(storedStep, 0), STEPS.length - 1) : 0;

    this.cardEl = document.createElement('div');
    this.cardEl.className = 'advisor-card';
    this.cardEl.hidden = true;
    hudRoot.appendChild(this.cardEl);

    this.beaconEl = document.createElement('div');
    this.beaconEl.className = 'advisor-beacon';
    this.beaconEl.hidden = true;
    hudRoot.appendChild(this.beaconEl);

    this.helpEl = document.createElement('div');
    this.helpEl.className = 'help-overlay';
    this.helpEl.hidden = true;
    this.helpEl.innerHTML = HELP_HTML;
    hudRoot.appendChild(this.helpEl);
    this.helpEl.querySelector('#help-close')!.addEventListener('click', () => this.closeHelp());
    this.helpEl.querySelector('#help-close-2')!.addEventListener('click', () => this.closeHelp());

    // If re-mounted mid-progress, silently fast-forward past whatever the
    // current game state already satisfies rather than replaying steps the
    // player has effectively already completed.
    const s = this.session.state;
    if (s) {
      let advanced = false;
      while (this.stepIndex < STEPS.length - 1 && STEPS[this.stepIndex].done(s, this.session.myPlayer)) {
        this.stepIndex++;
        advanced = true;
      }
      if (advanced) this.persistStep();
    }
  }

  /** Wired to the HUD's Help button: reopen the advisor (if solo) and show the quick reference. */
  openHelp(): void {
    this.dismissed = false;
    safeSet(DISMISS_KEY, '0');
    this.helpEl.hidden = false;
  }

  private closeHelp(): void {
    this.helpEl.hidden = true;
  }

  private persistStep(): void {
    safeSet(STEP_KEY, String(this.stepIndex));
  }

  private dismiss(): void {
    this.dismissed = true;
    safeSet(DISMISS_KEY, '1');
    this.cardEl.hidden = true;
    this.beaconEl.hidden = true;
  }

  private shouldShow(s: GameState): boolean {
    if (this.dismissed) return false;
    if (this.session.mode !== 'solo') return false;
    if (s.winner !== null) return false;
    return true;
  }

  /** Call once per animation frame. */
  update(): void {
    const s = this.session.state;
    if (!s || !this.shouldShow(s)) {
      this.cardEl.hidden = true;
      this.beaconEl.hidden = true;
      return;
    }

    // Step-advancement check: throttled to once per sim-second, purely by
    // reading state — never by observing which command the player issued.
    if (s.tick !== this.lastEvalTick && s.tick % TICKS_PER_SECOND === 0) {
      this.lastEvalTick = s.tick;
      if (Date.now() >= this.flashUntil && this.stepIndex < STEPS.length - 1) {
        if (STEPS[this.stepIndex].done(s, this.session.myPlayer)) {
          this.flashTitle = STEPS[this.stepIndex].title;
          this.flashUntil = Date.now() + FLASH_MS;
          this.stepIndex++;
          this.persistStep();
        }
      }
    }

    // Rebuild the card's DOM at the sim tick rate (8/sec), not once per
    // animation frame — otherwise the close button (and its listener) would
    // be torn down and recreated up to 60x/sec for no visible benefit.
    if (s.tick !== this.lastRenderTick) {
      this.lastRenderTick = s.tick;
      this.render(s);
    }
    this.positionBeacon(s);
  }

  private positionCard(): void {
    const bar = this.hudRoot.querySelector<HTMLElement>('.resource-bar');
    const top = bar ? Math.round(bar.getBoundingClientRect().bottom) + 8 : 60;
    this.cardEl.style.top = `${top}px`;
  }

  private render(s: GameState): void {
    this.cardEl.hidden = false;
    this.positionCard();

    if (Date.now() < this.flashUntil) {
      this.cardEl.innerHTML = `<div class="advisor-flash">&#10003; ${escapeHtml(this.flashTitle)} — complete</div>`;
      return;
    }

    const step = STEPS[this.stepIndex];
    const me = this.session.myPlayer;
    this.cardEl.innerHTML = `
      <div class="advisor-head">
        <span class="advisor-label">ADVISOR</span>
        <button class="advisor-close" id="advisor-close" aria-label="Skip tutorial" title="Skip tutorial">&#10005;</button>
      </div>
      <div class="advisor-title">${escapeHtml(step.title)}</div>
      <div class="advisor-tip">${escapeHtml(step.tip)}</div>
      <div class="advisor-progress">${escapeHtml(step.progress(s, me))}</div>
    `;
    this.cardEl.querySelector('#advisor-close')!.addEventListener('click', () => this.dismiss());
  }

  private positionBeacon(s: GameState): void {
    const step = STEPS[this.stepIndex];
    if (Date.now() < this.flashUntil || !step.beacon) {
      this.beaconEl.hidden = true;
      return;
    }
    const target = step.beacon(s, this.session.myPlayer);
    if (!target) {
      this.beaconEl.hidden = true;
      return;
    }
    const cam = this.session.camera;
    const { x, y } = worldToScreen(cam, target.x, target.y);
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement | null;
    const w = canvas?.clientWidth ?? window.innerWidth;
    const h = canvas?.clientHeight ?? window.innerHeight;
    if (x < -40 || y < -40 || x > w + 40 || y > h + 40) {
      this.beaconEl.hidden = true;
      return;
    }
    const size = effectiveTileSize(cam) * 1.8;
    this.beaconEl.hidden = false;
    this.beaconEl.style.left = `${x}px`;
    this.beaconEl.style.top = `${y}px`;
    this.beaconEl.style.width = `${size}px`;
    this.beaconEl.style.height = `${size}px`;
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage unavailable (private mode / disabled) — advisor just won't persist.
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
